import React, { Component } from "react";
import { toast } from "react-toastify";

import DateTimeService from "../../../../utils/dateTimeService";
import { petrolRequest, petrolRequestEdit } from "../../../../utils/apiRoutes";
import { getHeaders } from "../../../../utils/apiHelper";

import PetrolDetailDataTable from "./PetrolDetailDataTable";
import FormsRow from "../components/FormsRow";
import DateInput from "../components/DateInput";
import SelectInput from "../components/SelectInput";
import TextInput from "../components/TextInput";
import FileInput from "../components/FileInput";
import LoadingIndicator from "../../../Common/components/LoadingIndicator";
import ApprovalForm from "../../../Common/ApprovalForm";
import {
  getCookieData,
  getWorkFlowStatus,
  havePermissionForAmount,
} from "../../../../utils/CommonFunction";

export default class PetrolForm extends Component {
  constructor(props) {
    super(props);
    const toEditData = this.props.petrolApplicationData;

    // Default initial data for "Add New" mode
    const petrolApplicationData = {
      request_date: new DateTimeService().todayDate,
      vehicle_type: null,
      plate_number: "",
      withdrawal_location: null,
    };

    // For "Edit" mode, overwrite and prefill initial data
    if (!!toEditData) {
      const vehicleType = this.props.petrolVehicleTypes.find(
        (type) => type.value === toEditData.vehicle_type
      );
      const withdrawalLocation = this.props.withdrawalLocations.find(
        (loc) => loc.value === toEditData.withdrawal_location
      );
      petrolApplicationData["request_date"] = toEditData.request_date;
      petrolApplicationData["vehicle_type"] = vehicleType;
      petrolApplicationData["plate_number"] = toEditData.plate_number;
      petrolApplicationData["withdrawal_location"] = withdrawalLocation;
      petrolApplicationData["status"] = toEditData.status;
    }

    this.state = {
      user_info: getCookieData("user_info"),
      isLoading: false,
      isSaving: false,
      petrolApplicationData,
      petrolDetailsData: [],
      detailData: {
        petrol_application_detail_id: null,
        actual_date: new DateTimeService().todayDate,
        start_location: "",
        destination: "",
        trip_type: 0,
        purpose: "",
        remark: "",
        start_mileage: 0,
        end_mileage: 0,
        personal_use_km: 0,
      },
      newDoc: [],
      oldDocs: [], // For "Edit" mode, documents that are already uploaded on server
      work_flow_status: {},
    };
  }

  get totalKmDetail() {
    const detailData = this.state.detailData;
    return detailData.end_mileage - detailData.start_mileage;
  }

  get companyUsedKmDetail() {
    const detailData = this.state.detailData;
    return this.totalKmDetail - detailData.personal_use_km;
  }

  get effectiveAmountPerKm() {
    const vehicleType = Number(
      this.state.petrolApplicationData.vehicle_type?.value
    );

    let amountPerKm = 0;
    if (vehicleType === 1) {
      amountPerKm = Number(this.props.amountPerKmCar);
    } else if (vehicleType === 2) {
      amountPerKm = Number(this.props.amountPerKmBike);
    }

    return Number.isFinite(amountPerKm) ? amountPerKm : 0;
  }

  get totalAmountDetail() {
    return this.companyUsedKmDetail * this.effectiveAmountPerKm;
  }

  get totalKilometer() {
    return this.state.petrolDetailsData.reduce((total, detail) => {
      return (
        total +
        (detail.end_mileage - detail.start_mileage - detail.personal_use_km)
      );
    }, 0);
  }

  get totalAmount() {
    return this.totalKilometer * this.effectiveAmountPerKm;
  }

  async componentDidMount() {
    // For "Edit" mode, get petrol details data
    if (this.props.petrolApplicationData) {
      const responseData = await this.getPetrolDetails();
      const workflow = await this.getWorkflow();

      if (!!responseData && responseData.data) {
        const { details, documents } = responseData.data;
        this.setState({
          isLoading: false,
          petrolDetailsData: details,
          oldDocs: documents,
          work_flow_status: workflow,
        });
      }
    }
  }

  getPetrolDetails = async () => {
    const { petrol_application_id } = this.props.petrolApplicationData;
    this.setState({
      isLoading: true,
    });
    const res = await fetch(`${petrolRequest}/${petrol_application_id}`, {
      headers: getHeaders(),
    });
    if (res.ok) {
      return res.json();
    } else {
      this.setState({
        isLoading: false,
      });
      this.showError("Something went wrong... Couldn't load petrol details!");
      return null;
    }
  };

  getWorkflow = async () => {
    const { createdBy } = this.props.petrolApplicationData;
    return await getWorkFlowStatus(
      createdBy,
      this.state.user_info.user_id,
      "Petrol Request",
      "Allowance"
    );
  };

  handlePetrolApplicationDataChange = (value, inputKey) => {
    let data = this.state.petrolApplicationData;
    data[inputKey] = value;
    this.setState({
      petrolApplicationData: data,
    });
  };

  handleDetailDataChange = (value, inputKey) => {
    let data = this.state.detailData;
    data[inputKey] = value;
    this.setState({
      detailData: data,
    });
  };

  handleAddPetrolDetailsData = () => {
    const detailData = this.state.detailData;
    if (this.totalAmountDetail <= 0) {
      return this.showError("Amount must be greater than 0!");
    }
    if (detailData.start_location.trim().length === 0) {
      return this.showError("Please provide the start location!");
    }
    if (detailData.destination.trim().length === 0) {
      return this.showError("Please provide the destination!");
    }
    if (!detailData.trip_type) {
      return this.showError("Please select a trip type!");
    }
    if (detailData.purpose.trim().length === 0) {
      return this.showError("Please provide the reason / purpose!");
    }

    const petrolDetailsData = [...this.state.petrolDetailsData];

    // If there is detail ID, user is editing a petrol detail
    // Then we find the existing index, remove the original and replace it with the updated one
    if (!!detailData.petrol_application_detail_id) {
      const index = petrolDetailsData.findIndex(
        (detail) =>
          detail.petrol_application_detail_id ===
          detailData.petrol_application_detail_id
      );
      petrolDetailsData.splice(index, 1, {
        ...this.state.detailData,
      });
    } else {
      // Otherwise, user is adding a new petrol detail, then we simply push it
      petrolDetailsData.push({
        ...this.state.detailData,
        petrol_application_detail_id: new Date().toISOString(),
      });
    }
    this.setState({
      petrolDetailsData: petrolDetailsData,
    });
    this.clearDetailsForm();
  };

  clearDetailsForm = () => {
    this.setState({
      detailData: {
        petrol_application_detail_id: null,
        actual_date: new DateTimeService().todayDate,
        start_location: "",
        destination: "",
        trip_type: 0,
        purpose: "",
        remark: "",
        start_mileage: 0,
        end_mileage: 0,
        personal_use_km: 0,
      },
    });
  };

  handleRemovePetrolDetailsData = (index) => {
    const petrolDetailsData = this.state.petrolDetailsData;
    const deleted = petrolDetailsData.splice(index, 1);
    if (
      deleted &&
      deleted.length > 0 &&
      deleted[0].petrol_application_detail_id ===
        this.state.detailData.petrol_application_detail_id
    ) {
      // If the removed detail is what the user is editing
      this.clearDetailsForm();
    }
    this.setState({
      petrolDetailsData: petrolDetailsData,
    });
  };

  handleEditPetrolDetailsData = (index) => {
    const data = this.state.petrolDetailsData[index];
    const initialValues = {
      petrol_application_detail_id: data.petrol_application_detail_id,
      actual_date: data.actual_date,
      start_location: data.start_location,
      destination: data.destination,
      trip_type: data.trip_type,
      purpose: data.purpose,
      remark: data.remark,
      start_mileage: data.start_mileage,
      end_mileage: data.end_mileage,
      personal_use_km: data.personal_use_km,
    };

    this.setState({
      detailData: initialValues,
    });
  };

  handleSave = async (_, formApprovalData) => {
    const originalData = this.props.petrolApplicationData;
    const isEdit = !!originalData;

    const petrolApplicationData = this.state.petrolApplicationData;
    if (!petrolApplicationData.vehicle_type) {
      return this.showError("Please select a vehicle type!");
    }
    if (petrolApplicationData.plate_number.trim().length === 0) {
      return this.showError("Please provide a plate number!");
    }
    if (!petrolApplicationData.withdrawal_location) {
      return this.showError("Please select a withdrawal location!");
    }
    const petrolDetailsData = this.state.petrolDetailsData;
    if (petrolDetailsData.length === 0) {
      return this.showError("Please add at least one trip!");
    }

    const formdata = new FormData();
    const data = {
      request_date: petrolApplicationData.request_date,
      vehicle_type: petrolApplicationData.vehicle_type.value,
      plate_number: petrolApplicationData.plate_number,
      withdrawal_location: petrolApplicationData.withdrawal_location.value,
    };
    if (isEdit) {
      data["petrol_application_id"] = originalData.petrol_application_id;
    }
    const details = petrolDetailsData.map((detail) => {
      let detailId = detail.petrol_application_detail_id;
      if (typeof detailId === "string") {
        // Inside handleAddPetrolDetailsDatax method, we add DUMMY ID with date string.
        // This DUMMY ID is for temporary purpose (for editing petrol details)
        // We must not include this DUMMY ID when sending data to backend
        detailId = undefined;
      }

      return {
        petrol_application_detail_id: detailId,
        actual_date: detail.actual_date,
        start_location: detail.start_location,
        destination: detail.destination,
        trip_type: detail.trip_type.value,
        purpose: detail.purpose,
        remark: detail.remark,
        start_mileage: +detail.start_mileage,
        end_mileage: +detail.end_mileage,
        personal_use_km: +detail.personal_use_km,
      };
    });
    for (var i = 0; i < this.state.newDoc.length; i++) {
      var imagedata = this.state.newDoc[i];

      formdata.append("uploadfile", imagedata);
    }
    if (this.state.oldDocs && this.state.oldDocs.length > 0) {
      formdata.append("oldDocs", JSON.stringify(this.state.oldDocs));
    }
    formdata.append("petrolData", JSON.stringify(data));
    formdata.append("array", JSON.stringify(details));

    let isRequestForReferback = false;

    if (!!originalData) {
      const isCreator = this.state.user_info.user_id === originalData.createdBy;
      isRequestForReferback = originalData.status === 5 && isCreator;
    }

    if (isEdit && (formApprovalData || isRequestForReferback)) {
      if (isRequestForReferback) {
        // When the user resubmits after referbacked
        formdata.append(
          "formApprovalData",
          JSON.stringify({
            form_no: `PR${originalData.form_no}`,
            status_title: "requested",
            user_id: originalData.createdBy,
          })
        );
      } else {
        formdata.append("formApprovalData", JSON.stringify(formApprovalData));
      }
    }

    this.setState({
      isSaving: true,
    });
    try {
      const res = await fetch(isEdit ? petrolRequestEdit : petrolRequest, {
        method: "POST",
        headers: getHeaders(),
        body: formdata,
      });
      const responseData = await res.json();

      this.setState({
        isSaving: false,
      });

      if (!res.ok) {
        if (responseData.status === "error" && !!responseData.message) {
          return this.showError(responseData.message);
        }
        return this.showError("Something went wrong!");
      }

      if (responseData.message) {
        toast.success(responseData.message);
      } else {
        toast.success("Your information has been saved successfully!");
      }
      window.location.reload();
    } catch (error) {
      this.showError("Something went wrong! Please try again later.");
    }
  };

  handlefileChanged(_) {
    let newDoc = this.state.newDoc;
    var obj = document.querySelector("#petrolCRDrop").files.length;
    for (var j = 0; j < obj; j++) {
      var getfile = document.querySelector("#petrolCRDrop").files[j];
      newDoc.push(getfile);
    }
    document.querySelector("#petrolCRDrop").value = "";
    this.setState({
      newDoc: newDoc,
    });
  }

  removeOldDocument(index, event) {
    var array = this.state.oldDocs;
    array.splice(index, 1);
    this.setState({
      oldDocs: array,
    });
  }

  removeNewDocument(index, event) {
    var array = this.state.newDoc;
    array.splice(index, 1);
    this.setState({
      newDoc: array,
    });
  }

  check = (statusTitle, comment) => {
    // handleSave is called in two places. To have consistency, pass "Form Approval Log Data" as second parameter.
    this.handleSave(null, {
      form_no: `PR${this.props.petrolApplicationData.form_no}`,
      status_title: statusTitle,
      user_id: this.props.petrolApplicationData.createdBy,
      comment,
    });
  };

  showError = (message) => {
    this.props.showError(message);
  };

  render() {
    const petrolApplicationData = this.state.petrolApplicationData;
    const detailData = this.state.detailData;

    const isEdit = !!this.props.petrolApplicationData;

    const hasFetchedData =
      !this.state.isLoading &&
      (!isEdit || this.state.petrolDetailsData.length > 0);

    const isEditingDetail =
      !!this.state.detailData.petrol_application_detail_id;

    // isMainRole means: is it "Checker", "Verifier" or "Approver"
    const isMainRole = isEdit
      ? havePermissionForAmount(
          this.state.work_flow_status,
          this.props.petrolApplicationData.createdBy
        )
      : false;

    return (
      <div className="row white-bg">
        <div className="form-horizontal" name="demo-form">
          <div className="col-md-12" style={{ marginTop: 20 }}>
            <div className="ibox float-e-margins" id="add_check_form">
              <div className="ibox-content p-md">
                <FormsRow
                  style={{
                    paddingBottom: 35,
                    marginBottom: 35,
                    borderBottom: "solid 1px",
                  }}
                >
                  <TextInput
                    widthRatio={3}
                    label="Request Date"
                    value={petrolApplicationData.request_date}
                  />
                  <SelectInput
                    widthRatio={3}
                    label="Vehicle Type"
                    value={petrolApplicationData.vehicle_type}
                    onChange={this.handlePetrolApplicationDataChange}
                    inputKey="vehicle_type"
                    options={this.props.petrolVehicleTypes}
                  />
                  <TextInput
                    widthRatio={3}
                    label="Plate Number"
                    value={petrolApplicationData.plate_number}
                    onChange={this.handlePetrolApplicationDataChange}
                    inputKey="plate_number"
                  />
                  <SelectInput
                    widthRatio={3}
                    label="Withdrawal Location"
                    value={petrolApplicationData.withdrawal_location}
                    onChange={this.handlePetrolApplicationDataChange}
                    inputKey="withdrawal_location"
                    options={this.props.withdrawalLocations}
                  />
                </FormsRow>

                {hasFetchedData && (
                  <>
                    <FormsRow>
                      <DateInput
                        widthRatio={2}
                        label="Actual Date"
                        value={detailData.actual_date}
                        onChange={this.handleDetailDataChange}
                        inputKey="actual_date"
                      />
                      <TextInput
                        widthRatio={2}
                        label="Start Location"
                        value={detailData.start_location}
                        onChange={this.handleDetailDataChange}
                        inputKey="start_location"
                      />
                      <TextInput
                        widthRatio={2}
                        label="Destination"
                        value={detailData.destination}
                        onChange={this.handleDetailDataChange}
                        inputKey="destination"
                      />
                      <SelectInput
                        widthRatio={2}
                        label="Trip Type"
                        value={detailData.trip_type}
                        onChange={this.handleDetailDataChange}
                        inputKey="trip_type"
                        options={this.props.petrolTripTypes}
                      />
                      <TextInput
                        widthRatio={4}
                        label="Reason / Purpose"
                        value={detailData.purpose}
                        onChange={this.handleDetailDataChange}
                        inputKey="purpose"
                      />
                    </FormsRow>
                    <FormsRow className="margin-top-20">
                      <TextInput
                        widthRatio={2}
                        label="Start Mileage"
                        value={detailData.start_mileage}
                        onChange={this.handleDetailDataChange}
                        inputKey="start_mileage"
                        isNumber
                      />
                      <TextInput
                        widthRatio={2}
                        label="End Mileage"
                        value={detailData.end_mileage}
                        onChange={this.handleDetailDataChange}
                        inputKey="end_mileage"
                        isNumber
                      />
                      <TextInput
                        widthRatio={2}
                        label="Total (km)"
                        value={this.totalKmDetail}
                        isNumber
                      />
                      <TextInput
                        widthRatio={2}
                        label="Personal Used (km)"
                        value={detailData.personal_use_km}
                        onChange={this.handleDetailDataChange}
                        inputKey="personal_use_km"
                        isNumber
                      />
                      <TextInput
                        widthRatio={2}
                        label="Company Used (km)"
                        value={this.companyUsedKmDetail}
                        isNumber
                      />
                      <TextInput
                        widthRatio={2}
                        label="Amount"
                        value={this.totalAmountDetail}
                        isNumber
                      />
                    </FormsRow>
                    <FormsRow className="margin-top-20">
                      <TextInput
                        widthRatio={4}
                        label="Remark"
                        value={detailData.remark}
                        onChange={this.handleDetailDataChange}
                        inputKey="remark"
                      />
                      <div className="col-md-12 btn-rightend">
                        {isEditingDetail && (
                          <button
                            className="btn-danger btn"
                            onClick={this.clearDetailsForm}
                            style={{ marginTop: 10, marginRight: 15 }}
                            disabled={this.state.isSaving}
                          >
                            Cancel
                          </button>
                        )}
                        <button
                          className="btn-primary btn"
                          onClick={this.handleAddPetrolDetailsData}
                          style={{ marginTop: 10 }}
                          disabled={this.state.isSaving}
                        >
                          {isEditingDetail ? "Update" : "Add"}
                        </button>
                      </div>
                    </FormsRow>
                  </>
                )}

                {this.state.isLoading && (
                  <LoadingIndicator
                    marginTop={35}
                    marginBottom={35}
                    message="Loading Petrol Details..."
                  />
                )}
                {hasFetchedData && (
                  <PetrolDetailDataTable
                    petrolDetailsData={this.state.petrolDetailsData}
                    amountPerKm={this.effectiveAmountPerKm}
                    recalculateAmount
                    onRemoveDetail={this.handleRemovePetrolDetailsData}
                    onEditDetail={this.handleEditPetrolDetailsData}
                    editingDetailId={
                      this.state.detailData.petrol_application_detail_id
                    }
                  />
                )}

                {hasFetchedData && (
                  <>
                    <FormsRow>
                      <div className="col-md-12 btn-rightend">
                        <TextInput
                          widthRatio={2}
                          label="Total Kilometer"
                          value={this.totalKilometer}
                          isNumber
                        />
                        <TextInput
                          widthRatio={2}
                          label="Total Amount"
                          value={this.totalAmount}
                          isNumber
                        />
                      </div>
                    </FormsRow>
                    <FileInput
                      onFileChanged={this.handlefileChanged.bind(this)}
                      newDoc={this.state.newDoc}
                      onRemoveDocument={this.removeNewDocument.bind(this)}
                      onRemoveOldDocument={this.removeOldDocument.bind(this)}
                      oldDocs={this.state.oldDocs}
                    />
                    <div className="row" style={{ marginBottom: 150 }}>
                      {isMainRole ? (
                        <div className="col-md-12 btn-rightend mt20">
                          <ApprovalForm
                            approvalStatus={this.check.bind(this)}
                            status={this.state.petrolApplicationData.status}
                            work_flow={this.state.work_flow_status}
                            total_amount={this.totalAmount}
                          />
                        </div>
                      ) : !isEdit ||
                        (this.state.petrolApplicationData.status === 5 &&
                          this.props.petrolApplicationData.createdBy ===
                            this.state.user_info.user_id) ? (
                        <div className="col-md-12 btn-rightend mt20">
                          <button
                            onClick={this.handleSave.bind(this)}
                            id="saving_button"
                            className="btn btn-primary"
                            disabled={this.state.isSaving}
                          >
                            <span>{isEdit ? "Confirm" : "Save"}</span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
